import type { ReactNode } from "react";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";

export interface PluginStatus {
  state: "active" | "success" | "warning" | "error" | "inactive" | "loading";
  badge?: string | number;
  summary?: string;
}

export interface DevToolsPlugin {
  id: string;
  name: string;
  icon: IconDefinition;
  render: () => ReactNode;
  useStatus?: () => PluginStatus;
}

export interface ToolGroup {
  kind: "group";
  name: string;
  plugins: DevToolsPlugin[];
}

export interface Separator {
  kind: "separator";
}

export type DevToolsItem = DevToolsPlugin | ToolGroup | Separator;

export type SidebarEntry =
  | { type: "plugin"; plugin: DevToolsPlugin; groupName?: string }
  | { type: "separator" };

export function toolGroup(name: string, plugins: DevToolsPlugin[]): ToolGroup {
  return { kind: "group", name, plugins };
}

export function separator(): Separator {
  return { kind: "separator" };
}

// Flattens the heterogeneous config into an ordered render model (entries)
// and the flat plugin list the drawer + status aggregation rely on.
// Leading, trailing and consecutive separators are collapsed so the sidebar
// never shows a stray or doubled divider.
export function buildSidebarModel(items: DevToolsItem[]): {
  entries: SidebarEntry[];
  plugins: DevToolsPlugin[];
} {
  const entries: SidebarEntry[] = [];
  const plugins: DevToolsPlugin[] = [];

  for (const item of items) {
    if ("kind" in item) {
      if (item.kind === "separator") {
        if (entries.length === 0) continue;
        if (entries[entries.length - 1].type === "separator") continue;
        entries.push({ type: "separator" });
      } else {
        for (const plugin of item.plugins) {
          entries.push({ type: "plugin", plugin, groupName: item.name });
          plugins.push(plugin);
        }
      }
    } else {
      entries.push({ type: "plugin", plugin: item });
      plugins.push(item);
    }
  }

  if (entries.length > 0 && entries[entries.length - 1].type === "separator") {
    entries.pop();
  }

  return { entries, plugins };
}

export interface DevToolsState {
  isDrawerOpen: boolean;
  isSidebarExpanded: boolean;
  activePluginId: string | null;
  drawerWidth: number;
}

export interface DevToolsContextValue extends DevToolsState {
  plugins: DevToolsPlugin[];
  entries: SidebarEntry[];
  setActivePlugin: (id: string | null) => void;
  toggleDrawer: () => void;
  toggleSidebar: () => void;
}

const STORAGE_KEY = "chopin-devtools-state";
// Dedicated cookie (NOT the Rails session) so the server can read the sidebar
// open/closed state and seed the SSR markup with the correct class — avoids the
// animate-open flash on reload. Host-only + Lax so it never crosses subdomains.
const SIDEBAR_COOKIE = "chopin-devtools-sidebar";
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 year

export const DEFAULT_STATE: DevToolsState = {
  isDrawerOpen: false,
  isSidebarExpanded: false,
  activePluginId: null,
  drawerWidth: 320,
};

// `initialSidebarExpanded` comes from the server (read from SIDEBAR_COOKIE) and
// is passed identically to SSR and the first client render, so both agree on the
// sidebar class with no post-hydration animation. The cookie is authoritative
// for sidebar expansion; localStorage still backs the rest (drawer width etc).
export function loadPersistedState(initialSidebarExpanded = false): DevToolsState {
  if (typeof window === "undefined") {
    return { ...DEFAULT_STATE, isSidebarExpanded: initialSidebarExpanded };
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      // Drawer is ephemeral; never restore as open. Sidebar expansion comes from
      // the server cookie (via the prop), not localStorage, to match SSR.
      return {
        ...DEFAULT_STATE,
        ...parsed,
        isSidebarExpanded: initialSidebarExpanded,
        isDrawerOpen: false,
      };
    }
  } catch {
    // ignore corrupt storage
  }
  return { ...DEFAULT_STATE, isSidebarExpanded: initialSidebarExpanded };
}

export function persistState(state: DevToolsState): void {
  persistSidebarCookie(state.isSidebarExpanded);
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore storage errors
  }
}

// Mirror the sidebar bit into a server-readable cookie so the next page load
// renders the correct state server-side.
function persistSidebarCookie(expanded: boolean): void {
  if (typeof document === "undefined") return;
  const value = expanded ? "open" : "closed";
  document.cookie = `${SIDEBAR_COOKIE}=${value}; Path=/; SameSite=Lax; Max-Age=${SIDEBAR_COOKIE_MAX_AGE}`;
}
